"use client";

import { useState } from "react";

import { SelectWrapper } from "@/src/components/tasks/input-wrapper";

export interface ProviderSchedule {
  provider: string;
  initials: string;
  npi: string;
  schedule: {
    mon: { am: string; pm: string };
    tues: { am: string; pm: string };
    wed: { am: string; pm: string };
    thur: { am: string; pm: string };
    fri: { am: string; pm: string };
  };
}

interface ProviderScheduleTableProps {
  schedules: ProviderSchedule[];
  receivingLocationName: string;
  receivingProviderName: string;
  receivingProviderOptions: string[];
  receivingLocationOptions: string[];
  nameToScheduleNameMap: Map<string, string>;
}

function LocationCell({
  location,
  selectedLocation,
  nameToScheduleNameMap,
}: {
  location: string;
  selectedLocation: string;
  nameToScheduleNameMap: Map<string, string>;
}) {
  if (!location) return "";

  const shortSelectedLocation = nameToScheduleNameMap.get(selectedLocation);

  return (
    <>
      {location.split(" / ").map((loc, idx, arr) => (
        <span key={idx}>
          <span className={shortSelectedLocation && loc.trim() === shortSelectedLocation ? "font-bold" : ""}>
            {loc.trim()}
          </span>
          {idx < arr.length - 1 && " / "}
        </span>
      ))}
    </>
  );
}

export function ProviderScheduleTable({
  schedules,
  receivingLocationName,
  receivingProviderName,
  receivingProviderOptions,
  receivingLocationOptions,
  nameToScheduleNameMap,
}: ProviderScheduleTableProps) {
  const [selectedProvider, setSelectedProvider] = useState(receivingProviderName);
  const [selectedLocation, setSelectedLocation] = useState(receivingLocationName);

  const providerOptions = [
    { value: "ANY", label: "All Providers" },
    ...receivingProviderOptions.map((provider) => ({
      value: provider,
      label: provider,
    })),
  ];

  const locationOptions = [
    { value: "ANY", label: "All Locations" },
    ...receivingLocationOptions.map((location) => ({
      value: location,
      label: location,
    })),
  ];

  const filteredSchedules = schedules.filter(
    (schedule) => selectedProvider === "ANY" || schedule.provider === selectedProvider
  );

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <SelectWrapper
          value={selectedProvider}
          onChange={setSelectedProvider}
          label="Receiving Provider"
          placeholder="Select provider"
          options={providerOptions}
        />

        <SelectWrapper
          value={selectedLocation}
          onChange={setSelectedLocation}
          label="Receiving Location"
          placeholder="Select location"
          options={locationOptions}
        />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse border border-gray-300 text-sm table-fixed">
          <colgroup>
            <col className="w-28" />
            <col className="w-14" />
            <col className="w-18" />
            <col className="w-18" />
            <col className="w-18" />
            <col className="w-18" />
            <col className="w-18" />
            <col className="w-18" />
            <col className="w-18" />
            <col className="w-18" />
            <col className="w-18" />
            <col className="w-18" />
          </colgroup>
          <thead>
            <tr className="bg-muted">
              <th className="border border-gray-300 px-2 py-1 text-left font-medium text-foreground" rowSpan={2}>
                Provider
              </th>
              <th className="border border-gray-300 px-2 py-1 text-left font-medium text-foreground" rowSpan={2}>
                Initials
              </th>
              <th className="border border-gray-300 px-2 py-1 text-center font-medium text-foreground" colSpan={2}>
                Mon
              </th>
              <th className="border border-gray-300 px-2 py-1 text-center font-medium text-foreground" colSpan={2}>
                Tues
              </th>
              <th className="border border-gray-300 px-2 py-1 text-center font-medium text-foreground" colSpan={2}>
                Wed
              </th>
              <th className="border border-gray-300 px-2 py-1 text-center font-medium text-foreground" colSpan={2}>
                Thur
              </th>
              <th className="border border-gray-300 px-2 py-1 text-center font-medium text-foreground" colSpan={2}>
                Friday
              </th>
            </tr>
            <tr className="bg-muted/50">
              <th className="border border-gray-300 px-1 py-1 text-center font-medium text-xs text-foreground">AM</th>
              <th className="border border-gray-300 px-1 py-1 text-center font-medium text-xs text-foreground">PM</th>
              <th className="border border-gray-300 px-1 py-1 text-center font-medium text-xs text-foreground">AM</th>
              <th className="border border-gray-300 px-1 py-1 text-center font-medium text-xs text-foreground">PM</th>
              <th className="border border-gray-300 px-1 py-1 text-center font-medium text-xs text-foreground">AM</th>
              <th className="border border-gray-300 px-1 py-1 text-center font-medium text-xs text-foreground">PM</th>
              <th className="border border-gray-300 px-1 py-1 text-center font-medium text-xs text-foreground">AM</th>
              <th className="border border-gray-300 px-1 py-1 text-center font-medium text-xs text-foreground">PM</th>
              <th className="border border-gray-300 px-1 py-1 text-center font-medium text-xs text-foreground">AM</th>
              <th className="border border-gray-300 px-1 py-1 text-center font-medium text-xs text-foreground">PM</th>
            </tr>
          </thead>
          <tbody>
            {filteredSchedules.map((provider, index) => (
              <tr key={provider.provider} className={index % 2 === 0 ? "bg-background" : "bg-muted/40"}>
                <td className="border border-gray-300 px-2 py-1 font-medium bg-primary/10 text-foreground">
                  {provider.provider}
                </td>
                <td className="border border-gray-300 px-2 py-1 bg-primary/10 text-foreground">{provider.initials}</td>
                <td className="border border-gray-300 px-1 py-1 text-center text-xs break-words text-foreground">
                  <LocationCell
                    location={provider.schedule.mon.am}
                    selectedLocation={selectedLocation}
                    nameToScheduleNameMap={nameToScheduleNameMap}
                  />
                </td>
                <td className="border border-gray-300 px-1 py-1 text-center text-xs break-words text-foreground">
                  <LocationCell
                    location={provider.schedule.mon.pm}
                    selectedLocation={selectedLocation}
                    nameToScheduleNameMap={nameToScheduleNameMap}
                  />
                </td>
                <td className="border border-gray-300 px-1 py-1 text-center text-xs break-words text-foreground">
                  <LocationCell
                    location={provider.schedule.tues.am}
                    selectedLocation={selectedLocation}
                    nameToScheduleNameMap={nameToScheduleNameMap}
                  />
                </td>
                <td className="border border-gray-300 px-1 py-1 text-center text-xs break-words text-foreground">
                  <LocationCell
                    location={provider.schedule.tues.pm}
                    selectedLocation={selectedLocation}
                    nameToScheduleNameMap={nameToScheduleNameMap}
                  />
                </td>
                <td className="border border-gray-300 px-1 py-1 text-center text-xs break-words text-foreground">
                  <LocationCell
                    location={provider.schedule.wed.am}
                    selectedLocation={selectedLocation}
                    nameToScheduleNameMap={nameToScheduleNameMap}
                  />
                </td>
                <td className="border border-gray-300 px-1 py-1 text-center text-xs break-words text-foreground">
                  <LocationCell
                    location={provider.schedule.wed.pm}
                    selectedLocation={selectedLocation}
                    nameToScheduleNameMap={nameToScheduleNameMap}
                  />
                </td>
                <td className="border border-gray-300 px-1 py-1 text-center text-xs break-words text-foreground">
                  <LocationCell
                    location={provider.schedule.thur.am}
                    selectedLocation={selectedLocation}
                    nameToScheduleNameMap={nameToScheduleNameMap}
                  />
                </td>
                <td className="border border-gray-300 px-1 py-1 text-center text-xs break-words text-foreground">
                  <LocationCell
                    location={provider.schedule.thur.pm}
                    selectedLocation={selectedLocation}
                    nameToScheduleNameMap={nameToScheduleNameMap}
                  />
                </td>
                <td className="border border-gray-300 px-1 py-1 text-center text-xs break-words text-foreground">
                  <LocationCell
                    location={provider.schedule.fri.am}
                    selectedLocation={selectedLocation}
                    nameToScheduleNameMap={nameToScheduleNameMap}
                  />
                </td>
                <td className="border border-gray-300 px-1 py-1 text-center text-xs break-words text-foreground">
                  <LocationCell
                    location={provider.schedule.fri.pm}
                    selectedLocation={selectedLocation}
                    nameToScheduleNameMap={nameToScheduleNameMap}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
